<template>
    <div class="shop-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && loanShop.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="loanShop"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>

                <div v-if="editMode" class="card mt-3">
                    <div class="card-body">
                        <h6
                            class="col-12 mb-3 text-uppercase text-center bg-light p-2"
                        >
                            Active tags
                        </h6>
                        <filterable-tags is-loan :shop-id="id" />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <router-link
                                v-if="editMode"
                                :to="`/loan-shops/${id}/loan-products`"
                                class="btn btn-secondary btn-static waves-effect waves-light m-1"
                            >
                                <i class="fab fa-product-hunt" />
                                Products
                            </router-link>

                            <a
                                v-if="editMode"
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>

                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="loanShop"
                            :slug="loanShop.slug"
                            :options="{ slugBasicUrl: 'loan-shops' }"
                            :slug-error="slugError"
                            :featured-image="loanShop.featuredImageId"
                            :campuses="loanShop.campuses"
                            @meta-change="onMetaChange"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-campuses-change="onCampusesChange"
                            @on-featured-image-change="onFeaturedImageChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import { frontendUrl } from '@/config';
import AddEditForm from '@/components/loan-shops/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';
import FilterableTags from '@/components/product-tags/FilterableView';

export default {
    page: {
        title: 'Manage Shop'
    },

    validations: {
        loanShop: {
            name: {
                required
            },
            slug: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        FilterableTags,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit loan shop' : 'Add loan shop',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Loan Shops',
                    to: '/loan-shops'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            loanShop: {
                meta: {},
                campuses: []
            },
            informationKey: 0,
            serverErrors: [],
            loanShopValueStringify: ''
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                meta: true,
                featuredImage: true,
                campuses: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            return `${frontendUrl}/loan-shops/${this.loanShop.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.loanShop,
                this.serverErrors,
                'slug'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.loanShop = await this.getLoanShop(this.id);
                this.loanShopValueStringify = JSON.stringify(this.loanShop);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addLoanShop: 'loanShops/store',
            deleteLoanShop: 'loanShops/destroy',
            getLoanShop: 'loanShops/show',
            updateLoanShop: 'loanShops/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.loanShop.campuses.map(campus => campus.id);

            const data = {
                ...this.loanShop,
                campuses
            };

            try {
                if (!this.editMode) {
                    const added = await this.addLoanShop(data);

                    this.$router.push(`/loan-shops/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateLoanShop({
                        id: this.id,
                        data
                    });

                    this.loanShop.updatedAt = updatedAt;
                    this.loanShopValueStringify = JSON.stringify(this.loanShop);

                    this.$toaster('Loan shop updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteLoanShop(this.id);

                this.$router.push('/loan-shops');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (this.loanShopValueStringify !== JSON.stringify(this.loanShop)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.loanShop.meta = { ...meta };
        },

        onNameChange() {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(this.loanShop.name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.loanShop.slug = slug;
        },

        onFeaturedImageChange(imageId) {
            this.loanShop.featuredImageId = imageId;
        },

        onCampusesChange(campuses) {
            this.loanShop.campuses = campuses;
            this.informationKey++;
        }
    }
};
</script>
